import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AssignModal from "../../components/messaging/AssignModal";
import { SourceTaskChip, useTasksForRefs } from "../../components/messaging/sourceTasks";
import { transitionWorkItem } from "../../services/workItemsService";
import {
  listPaperwork, startFromTemplate, signPaperwork, uploadPaperworkVersion, renameOrderDoc, saveMySignature,
} from "../../services/orderPaperwork";
import { getSignedFilesUrl } from "../../utils/signedUrls";
import { paperworkTaskText } from "./paperworkTask";
import SignaturePad from "./SignaturePad";
import { isEditableForm } from "../../utils/paperworkForms";

export const APPROVED_MESSAGE = "Approved — go ahead and file it.";
const APPROVED_TEXT = "Approved — they have the go-ahead to file it.";

const STATUS = {
  detected: { label: "Blank", cls: "bg-slate-100 text-slate-600 ring-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-600" },
  filled: { label: "Filled — needs signature", cls: "bg-amber-50 text-amber-800 ring-amber-300 dark:bg-amber-500/10 dark:text-amber-200 dark:ring-amber-500/30" },
  signed: { label: "Signed", cls: "bg-emerald-50 text-emerald-700 ring-emerald-300 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30" },
};
const BTN = "text-xs font-semibold px-2.5 py-1 rounded-lg ring-1 ring-slate-300 dark:ring-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800/40 disabled:opacity-50";
const PRIMARY = "text-xs font-semibold px-2.5 py-1 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 text-white disabled:opacity-50";

// The Orders modal's Paperwork tab: this visit's forms — one row per form,
// whatever state it is in — and the clinic's blank templates. A form can be
// handed to a colleague as a task, filled in the editor or by hand (upload),
// and signed, or signed and approved, without opening it. Tasks link to a
// form as "<visitId>:<formId>"; only this visit's are asked about.
export default function PaperworkTab({
  patientId, patientName, schema, actorId, signerName = "", refreshKey = 0,
  openPaperworkId = null, onOpenedPaperwork, onOpenEditor, onSend,
}) {
  const [forms, setForms] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [flash, setFlash] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [renameText, setRenameText] = useState("");
  const [renameError, setRenameError] = useState(null);
  const [renameBusy, setRenameBusy] = useState(false);
  // { form, task } waiting on a signature drawn here (none saved yet).
  const [padFor, setPadFor] = useState(null);
  const uploadInput = useRef(null);
  const uploadFor = useRef(null);
  const openedFor = useRef(null);

  const reload = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const res = await listPaperwork({ patientId });
      setForms(res?.forms || []);
      setTemplates(res?.templates || []);
    } catch (err) {
      setFlash({ type: "error", text: `Could not load the paperwork — ${err?.message || err}` });
    } finally {
      setLoading(false);
    }
  }, [patientId]);
  useEffect(() => { reload(); }, [reload, refreshKey]);

  const refOf = useCallback((form) => `${patientId}:${form.id}`, [patientId]);
  const refs = useMemo(() => forms.map(refOf), [forms, refOf]);
  const { taskByRef, reloadTasks } = useTasksForRefs("order_paperwork", refs, "Orders → Paperwork");

  // Arriving from a task's "Open in chart": that form, in the editor when it
  // can be refilled and isn't signed yet (a signed one is there to be sent,
  // from the list). The parent is told the link is used, so Back from the
  // editor (which remounts this tab) shows the list.
  useEffect(() => {
    if (!openPaperworkId || openedFor.current === openPaperworkId) return;
    const form = forms.find((f) => f.id === openPaperworkId);
    if (!form) return;
    openedFor.current = openPaperworkId;
    onOpenedPaperwork?.();
    if (isEditableForm(form) && form.status !== "signed") onOpenEditor(form);
  }, [openPaperworkId, forms, onOpenEditor, onOpenedPaperwork]);

  const run = async (id, fn, okText) => {
    setBusyId(id);
    setFlash(null);
    try {
      await fn();
      if (okText) setFlash({ type: "success", text: okText });
    } catch (err) {
      // The signature pad says that one itself.
      if (!err?.payload?.needs_signature) setFlash({ type: "error", text: err?.message || String(err) });
    } finally {
      setBusyId(null);
    }
    // After a failure too: a step that did land (a signature) must show.
    await reload();
    reloadTasks();
  };

  const markSigned = (form, signed) => setForms((list) => list.map((f) => (
    f.id === form.id ? { ...f, ...(signed || {}), status: "signed" } : f
  )));

  // Signs the form's current file with the saved signature, or with one just
  // drawn. With none saved, the pad opens here — never the editor, which
  // would open the form's blank source rather than this file.
  const signForm = async (form, { task = null, signature = null } = {}) => {
    try {
      const res = await signPaperwork({ patientId, paperworkId: form.id, ...(signature ? { signature } : {}) });
      markSigned(form, res?.form);
    } catch (err) {
      if (err?.payload?.already_signed) {
        markSigned(form);
        return;
      }
      if (err?.payload?.needs_signature) setPadFor({ form, task });
      throw err;
    }
  };

  const sign = (form) => run(form.id, () => signForm(form), `Signed ${form.name}.`);

  const approveTask = (task) =>
    transitionWorkItem({ kind: "task", id: task.id }, "confirm", actorId, { message: APPROVED_MESSAGE });

  // Signs only what is not signed yet, so pressing it again after the task
  // step failed just finishes the task.
  const approve = (form, task) => run(form.id, async () => {
    if (form.status !== "signed") await signForm(form, { task });
    await approveTask(task);
  }, APPROVED_TEXT);

  const signWithDrawn = ({ png, signerName: typedName, remember }) => {
    const { form, task } = padFor;
    const name = typedName || signerName || "";
    setPadFor(null);
    run(form.id, async () => {
      await signForm(form, { task, signature: { png, signer_name: name } });
      if (remember) {
        // For next time; this form is signed either way.
        try {
          await saveMySignature({ png, signerName: name });
        } catch (err) {
          console.warn("Paperwork: signature not saved for reuse", err);
        }
      }
      if (task) await approveTask(task);
    }, task ? APPROVED_TEXT : `Signed ${form.name}.`);
  };

  const assignTemplate = (t) => run(t.path, async () => {
    const res = await startFromTemplate({ patientId, templatePath: t.path });
    setAssigning(res.form);
  });

  const signTemplate = (t) => run(t.path, async () => {
    const res = await startFromTemplate({ patientId, templatePath: t.path });
    await signForm(res.form);
  }, `Signed a blank ${t.label}.`);

  const pickUpload = (form) => {
    uploadFor.current = form;
    uploadInput.current?.click();
  };
  const onUploadPicked = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const form = uploadFor.current;
    if (!file || !form) return;
    run(form.id, () => uploadPaperworkVersion({ patientId, paperworkId: form.id, file }),
      `Uploaded the filled ${form.name}.`);
  };

  // The bucket is private: a signed URL per click.
  const preview = async (form) => {
    setFlash(null);
    try {
      const url = await getSignedFilesUrl(form.path);
      if (!url) {
        setFlash({ type: "error", text: `Could not open ${form.name}.` });
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setFlash({ type: "error", text: `Could not open ${form.name} — ${err.message}` });
    }
  };

  const startRename = (form) => {
    setRenameError(null);
    setRenaming(form.id);
    setRenameText(form.name.replace(/\.pdf$/i, ""));
  };
  const cancelRename = () => {
    setRenaming(null);
    setRenameText("");
    setRenameError(null);
  };
  const commitRename = async (form) => {
    const next = renameText.trim();
    if (!next) {
      setRenameError("Give the order a name.");
      return;
    }
    setRenameBusy(true);
    setRenameError(null);
    try {
      const res = await renameOrderDoc({ patientId, path: form.path, filename: next });
      // Patch the one row rather than refetching: a reload would scroll the
      // tab back to the top.
      setForms((list) => list.map((f) => (
        f.id === form.id ? { ...f, path: res?.path || f.path, name: res?.filename || `${next}.pdf` } : f
      )));
      cancelRename();
    } catch (err) {
      setRenameError(err?.message || String(err));
    } finally {
      setRenameBusy(false);
    }
  };

  const formButtons = (form, task) => {
    const reviewing = !!task && task.status === "pending_review" && task.assigned_by === actorId;
    const busy = busyId === form.id;
    const doc = { path: form.path, filename: form.name, signer_name: form.signer_name, signed_at: form.signed_at };
    return (
      <div className="ml-auto shrink-0 flex flex-wrap items-center justify-end gap-1">
        {reviewing ? (
          <button type="button" disabled={busy} onClick={() => approve(form, task)} className={PRIMARY}>
            {form.status === "signed" ? "Approve" : "Sign & approve"}
          </button>
        ) : form.status !== "signed" && (
          <button type="button" disabled={busy} onClick={() => sign(form)} className={BTN}>✍️ Sign</button>
        )}
        {isEditableForm(form) && (
          <button type="button" disabled={busy} onClick={() => onOpenEditor(form)} className={BTN}>Fill out</button>
        )}
        <button type="button" disabled={busy} onClick={() => pickUpload(form)} className={BTN}>⬆️ Upload filled version</button>
        <button type="button" disabled={busy} onClick={() => setAssigning(form)} className={BTN}
                title="Hand this form to a colleague as a Messages task">
          📋 Assign as task
        </button>
        {form.status === "signed" && (
          <>
            <button type="button" onClick={() => onSend(doc, "fax")} className={BTN}>📠 Send as fax</button>
            <button type="button" onClick={() => onSend(doc, "email")} className={BTN}>✉️ Send as email</button>
          </>
        )}
        {/\.pdf$/i.test(form.path) && (
          <button type="button" aria-label={`Rename ${form.name}`} title="Rename" onClick={() => startRename(form)}
                  className="text-xs px-2 py-1 rounded-lg ring-1 ring-slate-300 dark:ring-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800/40">
            ✏️
          </button>
        )}
      </div>
    );
  };

  const renameBox = (form) => (
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-1">
        <input
          aria-label="New name"
          autoFocus
          value={renameText}
          disabled={renameBusy}
          onChange={(e) => setRenameText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitRename(form);
            if (e.key === "Escape") cancelRename();
          }}
          className="flex-1 min-w-0 text-sm px-2 py-1 rounded-md ring-1 ring-slate-300 dark:ring-slate-600 bg-white dark:bg-slate-900"
        />
        <span className="text-xs text-slate-400 shrink-0">.pdf</span>
        <button type="button" onClick={() => commitRename(form)} disabled={renameBusy} className={PRIMARY}>
          {renameBusy ? "Saving…" : "Save"}
        </button>
        <button type="button" onClick={cancelRename} disabled={renameBusy}
                className="shrink-0 text-xs px-2 py-1 rounded-lg ring-1 ring-slate-300 dark:ring-slate-600">
          Cancel
        </button>
      </div>
      {renameError && (
        <div role="alert" className="mt-1 text-[11px] font-semibold text-rose-700 dark:text-rose-300">{renameError}</div>
      )}
    </div>
  );

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <div className="text-xs font-semibold text-slate-600 dark:text-slate-300">Forms on this visit ({forms.length})</div>
        <button type="button" onClick={reload} disabled={loading}
                className="ml-auto text-[11px] px-2 py-1 rounded ring-1 ring-slate-300 dark:ring-slate-600 disabled:opacity-50">
          {loading ? "Loading…" : "↻ Refresh"}
        </button>
      </div>
      {padFor && (
        <div className="mb-3 rounded-lg ring-1 ring-amber-300 dark:ring-amber-500/40 p-3">
          <div className="text-xs font-semibold text-amber-800 dark:text-amber-200 mb-2">
            No saved signature — draw one to sign {padFor.form.name}.
          </div>
          <SignaturePad signerName={signerName} onSave={signWithDrawn} onCancel={() => setPadFor(null)} />
        </div>
      )}
      {flash && (
        <div role={flash.type === "error" ? "alert" : "status"}
             className={`mb-2 text-xs rounded-md px-2 py-1 ${flash.type === "error"
               ? "bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300"
               : "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"}`}>
          {flash.text}
        </div>
      )}
      {forms.length === 0 ? (
        <div className="py-6 text-center text-sm text-slate-500 dark:text-slate-400">
          No forms on this visit yet.
          <div className="mt-1 text-xs">
            Start one from a blank template below, or tick orders on the other tabs and press Generate order.
          </div>
        </div>
      ) : (
        <ul className="space-y-2">
          {forms.map((form) => {
            const task = taskByRef[refOf(form)];
            const status = STATUS[form.status] || STATUS.detected;
            return (
              <li key={form.id} data-testid={`paperwork-form-${form.name}`}
                  className="flex items-start gap-3 rounded-lg ring-1 ring-slate-200 dark:ring-slate-700 px-3 py-2">
                <span className="text-lg shrink-0">📄</span>
                {renaming === form.id ? renameBox(form) : (
                  <>
                    <div className="min-w-0">
                      <button type="button" aria-label={`Preview ${form.name}`} title="Open the PDF"
                              onClick={() => preview(form)}
                              className="block max-w-full text-left text-sm font-semibold truncate hover:underline">
                        {form.name}
                      </button>
                      <div className="flex flex-wrap items-center gap-2 mt-0.5">
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ring-1 ${status.cls}`}>{status.label}</span>
                        {form.status === "signed" && (
                          <span className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                            {form.signer_name ? `Signed by ${form.signer_name}` : "Signed"}
                            {form.signed_at ? ` · ${new Date(form.signed_at).toLocaleString()}` : ""}
                          </span>
                        )}
                        {form.uploaded && <span className="text-[11px] text-slate-500 dark:text-slate-400">Uploaded</span>}
                      </div>
                      {task && <SourceTaskChip task={task} testId={`paperwork-task-${form.name}`} />}
                    </div>
                    {formButtons(form, task)}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {templates.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">Blank templates ({templates.length})</div>
          <ul className="space-y-2">
            {templates.map((t) => (
              <li key={t.path} data-testid={`paperwork-template-${t.name}`}
                  className="flex items-center gap-3 rounded-lg ring-1 ring-dashed ring-slate-300 dark:ring-slate-600 px-3 py-2">
                <span className="text-lg shrink-0">🗒️</span>
                <div className="min-w-0 text-sm font-semibold truncate">{t.label}</div>
                <div className="ml-auto shrink-0 flex flex-wrap items-center gap-1">
                  <button type="button" disabled={busyId === t.path} onClick={() => signTemplate(t)} className={BTN}>✍️ Sign</button>
                  <button type="button" disabled={busyId === t.path}
                          onClick={() => onOpenEditor({ template: true, path: t.path, name: t.name })} className={BTN}>
                    Fill out
                  </button>
                  <button type="button" disabled={busyId === t.path} onClick={() => assignTemplate(t)} className={BTN}>📋 Assign as task</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <input ref={uploadInput} type="file" className="hidden" aria-label="Filled version file"
             accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg" onChange={onUploadPicked} />

      {/* Chakra: its styles do not depend on #clinic-root, so it may portal. */}
      {assigning && (() => {
        const { title, description } = paperworkTaskText(assigning, patientName);
        return (
          <AssignModal
            isOpen
            onClose={() => setAssigning(null)}
            currentUser={{ id: actorId, schema }}
            taskOnly
            initialTitle={title}
            initialDescription={description}
            taskSource={{
              source_kind: "order_paperwork",
              source_ref: refOf(assigning),
              source_patient_name: patientName || null,
              source_doc_schema: schema || null,
            }}
            onAssigned={reloadTasks}
          />
        );
      })()}
    </div>
  );
}
